import React, { Suspense } from "react";
import styled, { css } from "styled-components";
import { Colors } from "../../../theme/colors";
import { FontSizes } from "../../../theme/fonts";
import Spinner from "../Spinner";

type ButtonVariation = "horizontal" | "vertical";
type ButtonType = "primary" | "primary-inverted" | "error-inverted";
type ButtonIcon = "edit" | "refresh" | "close";

interface ButtonProps {
  className?: string;
  type: ButtonType;
  variation?: ButtonVariation;
  loading?: boolean;
  label?: string;
  icon?: ButtonIcon;
  onClick?: () => void;
}

const Button = ({
  className,
  type,
  variation = "horizontal",
  label,
  icon,
  loading = false,
  onClick,
}: ButtonProps) => {
  const fireOnClick = onClick !== undefined && loading === false;
  const hasLabel = label !== undefined;

  return (
    <Container
      className={className}
      type={type}
      variation={variation}
      fireOnClick={fireOnClick}
      onClick={onClick}
    >
      {loading && <Spinner />}
      {!loading && (
        <>
          {label !== undefined && <Label type={type}>{label}</Label>}
          {icon !== undefined && (
            <Suspense fallback={null}>
              <IconSVG
                as={getIconSVG(icon)}
                type={type}
                variation={variation}
                hasLabel={hasLabel}
              />
            </Suspense>
          )}
        </>
      )}
    </Container>
  );
};

const getIconSVG = (icon: ButtonIcon) => {
  switch (icon) {
    case "edit":
      return EditSVG;
    case "refresh":
      return RefreshSVG;
    case "close":
      return CloseSVG;
  }
};

const Label = styled.div<{
  type: ButtonType;
}>`
  ${(props) => {
    let styles = css``;
    switch (props.type) {
      case "primary-inverted":
        styles = css`
          color: ${Colors.Primary};
        `;
        break;
      case "primary":
        styles = css`
          color: ${Colors.White};
        `;
        break;
      case "error-inverted":
        styles = css`
          color: ${Colors.Error};
        `;
        break;
    }
    return styles;
  }}

  font-weight: bold;
  font-size: ${FontSizes.Paragraph};
`;

const IconSVG = styled.div<{
  type: ButtonType;
  variation: ButtonVariation;
  hasLabel: boolean;
}>`
  ${(props) => {
    let styles = css``;
    switch (props.type) {
      case "primary-inverted":
        styles = css`
          fill: ${Colors.Primary};
        `;
        break;
      case "primary":
        styles = css`
          fill: ${Colors.White};
        `;
        break;
      case "error-inverted":
        styles = css`
          fill: ${Colors.Error};
        `;
        break;
    }

    if (props.hasLabel)
      switch (props.variation) {
        case "horizontal":
          styles = css`
            ${styles}
            margin-left: ${4 * 2}px;
          `;
          break;
        case "vertical":
          styles = css`
            ${styles}
            margin-top: ${4 * 2}px;
          `;
          break;
      }

    return styles;
  }}

  width: 21px;
  height: 21px;
  min-width: 21px;
  min-height: 21px;
`;

const Container = styled.div<{
  type: ButtonType;
  variation: ButtonVariation;
  fireOnClick: boolean;
}>`
  ${(props) => {
    let styles = css``;
    switch (props.type) {
      case "primary-inverted":
        styles = css`
          border-style: solid;
          border-width: 1px;
          border-color: ${Colors.Primary};
          &:hover {
            border-color: ${Colors.PrimaryHover};
          }
        `;
        break;
      case "primary":
        styles = css`
          background-color: ${Colors.Primary};
          &:hover {
            background-color: ${Colors.PrimaryHover};
          }
        `;
        break;
      case "error-inverted":
        styles = css`
          border-style: solid;
          border-width: 1px;
          border-color: ${Colors.Error};
          &:hover {
            border-color: ${Colors.Error};
          }
        `;
        break;
    }
    return styles;
  }}

  ${(props) => {
    let styles = css``;
    switch (props.variation) {
      case "horizontal":
        styles = css`
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: center;
        `;
        break;
      case "vertical":
        styles = css`
          display: flex;
          flex-direction: column;
          flex-wrap: nowrap;
          align-items: center;
          justify-content: center;
        `;
        break;
    }
    return styles;
  }}

  text-align: center;
  border-radius: 15px;
  padding: ${8 * 2}px;
  cursor: ${(props) => (props.fireOnClick ? "pointer" : "default")};
`;

const EditSVG = React.lazy(() => import("../Svg/EditSVG"));
const RefreshSVG = React.lazy(() => import("../Svg/RefreshSVG"));
const CloseSVG = React.lazy(() => import("../Svg/CloseSVG"));

export default Button;
